import { action, computed, makeObservable, observable, runInAction } from "mobx";
import Axios from "src/network/Axios";
import { CUSTOMER_FEATURE } from "src/api/customer";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey } from "src/network/User";
import { RootStore } from "src/stores/RootStore";

export enum SERVICE_ACTIVATION_PROCESS {
    START_SERVICE_ACTIVATION,
    SERVICE_ACTIVATION_RESULT
}

export enum SERVICE_ACTIVATION_PROCESS_STEP {
    START_SERVICE_ACTIVATION,
    SERVICE_ACTIVATION_RESULT
}

export enum ACTIVATION_STATUS {
    NONE,
    ACTIVATING,
    SUCCESS,
    FAILURE
}

interface IProcessStep {
    stepNumber: number;
    stepType: SERVICE_ACTIVATION_PROCESS_STEP;
    next?: () => IProcessStep | null;
    prev?: () => IProcessStep | null;
    parent: IProcess | null;
}

interface IProcess {
    type: SERVICE_ACTIVATION_PROCESS;
    processSteps: IProcessStep[];
}

interface IServiceActivationProcess {
    stepNumber: number;
    stepType: SERVICE_ACTIVATION_PROCESS_STEP;
    processes: IProcess[];
}

export class ServicesActivationProgressModalStore {
    rootStore: RootStore;

    ACTIVATION_MODAL_MAX_NUMBER_OF_STEPS = 2;

    currentProcessType: SERVICE_ACTIVATION_PROCESS = SERVICE_ACTIVATION_PROCESS.START_SERVICE_ACTIVATION;
    currentStepType: SERVICE_ACTIVATION_PROCESS_STEP = SERVICE_ACTIVATION_PROCESS_STEP.START_SERVICE_ACTIVATION;
    currentServiceActivationStatus: ACTIVATION_STATUS = ACTIVATION_STATUS.NONE;
    isActivationProgressModalDisplayed = false;
    isServiceSuccessfullyActivated = false;
    costMonth = "";
    serviceActivationProcess: IServiceActivationProcess = {
        stepNumber: 1,
        stepType: SERVICE_ACTIVATION_PROCESS_STEP.START_SERVICE_ACTIVATION,
        processes: [
            {
                type: SERVICE_ACTIVATION_PROCESS.SERVICE_ACTIVATION_RESULT,
                processSteps: [
                    {
                        stepNumber: 2,
                        stepType: SERVICE_ACTIVATION_PROCESS_STEP.SERVICE_ACTIVATION_RESULT,
                        next: function () {
                            return this.parent ? this.parent.processSteps[1] : null;
                        },
                        prev: function () {
                            return this.parent ? this.parent.processSteps[0] : null;
                        },
                        parent: null
                    }
                ]
            }
        ]
    };

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        // Assign parents post object creation
        for (let process of this.serviceActivationProcess.processes) {
            for (let step of process.processSteps) {
                step.parent = process;
            }
        }

        makeObservable(this, {
            currentProcessType: observable,
            currentStepType: observable,
            currentServiceActivationStatus: observable,
            isActivationProgressModalDisplayed: observable,
            isServiceSuccessfullyActivated: observable,
            costMonth: observable,

            getProcess: action,
            resetServiceActivationModal: action,
            resetCurrentProcessType: action,
            setCurrentProcessType: action,
            setCurrentStepType: action,
            setCurrentServiceActivationStatus: action,
            setIsActivationProgressModalDisplayed: action,
            setIsServiceSuccessfullyActivated: action,
            setCostMonth: action,

            processes: computed,
            currentProcess: computed,
            currentStep: computed
        });
    }

    getProcess = (processType: SERVICE_ACTIVATION_PROCESS) => {
        return this.serviceActivationProcess.processes.find((process) => {
            return process.type === processType;
        })!;
    };

    resetServiceActivationModal = () => {
        this.resetCurrentProcessType();
        this.currentServiceActivationStatus = ACTIVATION_STATUS.NONE;
    };

    resetCurrentProcessType() {
        this.currentProcessType = SERVICE_ACTIVATION_PROCESS.SERVICE_ACTIVATION_RESULT;
        this.currentStepType = SERVICE_ACTIVATION_PROCESS_STEP.SERVICE_ACTIVATION_RESULT;
    }

    setCurrentProcessType = (processType: SERVICE_ACTIVATION_PROCESS) => {
        this.currentProcessType = processType;
        this.currentStepType = this.getProcess(processType).processSteps[0].stepType;
    };

    setCurrentStepType = (stepType: SERVICE_ACTIVATION_PROCESS_STEP) => {
        this.currentStepType = stepType;
    };

    setIsActivationProgressModalDisplayed = (isModalDisplayed: boolean) => {
        this.isActivationProgressModalDisplayed = isModalDisplayed;

        if (!isModalDisplayed) {
            this.costMonth = "0";
        }
    };

    setCurrentServiceActivationStatus = (status: ACTIVATION_STATUS) => {
        this.currentServiceActivationStatus = status;
    };
    setIsServiceSuccessfullyActivated = (isServiceSuccessfullyActivated: boolean) => {
        this.isServiceSuccessfullyActivated = isServiceSuccessfullyActivated;
    };

    /* COMPUTED */
    get processes() {
        return this.serviceActivationProcess.processes;
    }

    get currentProcess(): IProcess | undefined {
        return this.serviceActivationProcess.processes.find((process) => process.type === this.currentProcessType);
    }

    get currentStep(): IProcessStep | undefined {
        return this.currentProcess?.processSteps.find((step) => step.stepType === this.currentStepType);
    }

    activateService = async (
        serviceid: string,
        isTermsAccepted: boolean,
        isActivationConfirmed: boolean | null,
        doesServiceCost: boolean | null | undefined
    ): Promise<boolean> => {
        this.setIsServiceSuccessfullyActivated(false);

        const defaultCostAccepted = doesServiceCost ? isActivationConfirmed ?? false : null;

        try {
            await Axios.getAuthInstance().post(
                `${NetworkConfig.activateService}${serviceid}`,
                {
                    conditions: {
                        licensedUnits: this.rootStore.navStore.customer.licencedunits || 0,
                        costMonth: this.costMonth
                    },
                    costaccepted: defaultCostAccepted,
                    termsaccepted: isTermsAccepted
                },
                {
                    urlWithoutParams: NetworkConfig.activateService,
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                }
            );
            this.setIsServiceSuccessfullyActivated(true);
            // customer features gate other services (KI-Belegerfassung needs Kredi Flow Pro)
            this.rootStore.authStore.setRefetchCustomerData(true);
        } catch (error) {
            console.error("Error while trying to call activate_service: ", error);
        }
        return this.isServiceSuccessfullyActivated;
    };

    getTicketingCostMonth = async (): Promise<string> => {
        let ticketingCostMonth = undefined;

        try {
            const { data } = await Axios.getAuthInstance().post(
                NetworkConfig.getTicketingCostMonth,
                {
                    licensedUnits: this.rootStore.navStore.customer.licencedunits || 0
                },
                {
                    responseType: "text",
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                }
            );
            ticketingCostMonth = data;
        } catch (error) {
            console.error("Error while trying to call getticketingcost: ", error);
        }

        return ticketingCostMonth || "";
    };

    setCostMonth = (feature: number) => {
        const costsMonthPromise = this.getTicketingCostMonth();
        this.costMonth = "0";

        costsMonthPromise
            .then((result) => {
                try {
                    const resultObject = JSON.parse(result);
                    let cost = resultObject.cost;

                    if (feature === CUSTOMER_FEATURE.KREDI_FLOW_PRO) {
                        cost = cost * 1.5;
                    }

                    const roundedCostValue = cost.toFixed(2);
                    runInAction(() => {
                        this.costMonth = roundedCostValue.toString();
                    });
                } catch (error) {
                    runInAction(() => {
                        this.costMonth = "Fehler";
                    });
                }
            })
            .catch((error) => {
                runInAction(() => {
                    this.costMonth = "Fehler";
                });
            });
    };
}
